/**
 * Custom Event Dispatcher for Kazakan Tools
 * Collects aggregated, anonymous tool lifecycle events.
 * 
 * Strict Resilience Guardrail:
 * Network failure, adblockers, or API downtime MUST NEVER throw unhandled
 * exceptions or interrupt tool execution.
 */

const VALID_EVENTS = new Set([
  'tool_view',
  'tool_started',
  'tool_completed',
  'tool_failed',
  'promo_clicked'
]);

/**
 * Intelligent Client Device Classification
 * Distinguishes between desktop, tablet, and mobile with high fidelity:
 * - Accurately detects modern iPads on iPadOS 13+ (which claim MacIntel in UA).
 * - Distinguishes Android tablets from Android phones based on viewport & UA hints.
 * - Leverages User-Agent Client Hints (navigator.userAgentData) when present.
 * - Utilizes CSS Interaction media queries (pointer: coarse/fine).
 */
export function detectClientDevice() {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return 'desktop';
  }

  try {
    const ua = navigator.userAgent || '';
    const maxTouch = navigator.maxTouchPoints || 0;

    // 1. Modern iPad / iPadOS 13+ detection (reports MacIntel with touch points)
    const isIPad = (navigator.platform === 'MacIntel' && maxTouch > 1) || /iPad/i.test(ua);
    if (isIPad) {
      return 'tablet';
    }

    // 2. Client viewport dimensions
    const width = Math.min(window.innerWidth || 0, window.screen?.width || 0) || window.innerWidth || 1024;
    const height = Math.min(window.innerHeight || 0, window.screen?.height || 0) || window.innerHeight || 768;
    const minDim = Math.min(width, height);
    const hasTouch = maxTouch > 0 || (window.matchMedia && window.matchMedia('(pointer: coarse)').matches);

    // 3. Android Tablet vs Phone
    if (/Android/i.test(ua)) {
      if (minDim >= 600 || !/Mobile/i.test(ua)) {
        return 'tablet';
      }
      return 'mobile';
    }

    // 4. Tablet UA signatures (Kindle, Silk, PlayBook)
    if (/(tablet|playbook|silk|kindle)|(android(?!.*mobi))/i.test(ua)) {
      return 'tablet';
    }

    // 5. Mobile UA signatures (iPhone, iPod, Android Mobile, Opera Mini)
    if (/Mobile|iPhone|iPod|IEMobile|BlackBerry|Opera Mini/i.test(ua)) {
      return 'mobile';
    }

    // 6. Modern Chromium Client Hints API
    if (navigator.userAgentData && typeof navigator.userAgentData.mobile === 'boolean') {
      if (navigator.userAgentData.mobile) {
        return minDim >= 600 ? 'tablet' : 'mobile';
      }
      return 'desktop';
    }

    // 7. Touch screen dimensions heuristic for hybrid/touch devices
    if (hasTouch && minDim < 600) {
      return 'mobile';
    }
    if (hasTouch && minDim >= 600 && Math.max(width, height) <= 1366 && window.matchMedia && window.matchMedia('(pointer: coarse)').matches) {
      return 'tablet';
    }

    return 'desktop';
  } catch {
    return 'desktop';
  }
}

/**
 * Detects whether the code is running on a local development/test server
 */
export function isLocalEnvironment() {
  if (typeof window === 'undefined' || !window.location) return false;
  const host = window.location.hostname || '';
  return /^(localhost|127\.0\.0\.1|0\.0\.0\.0|\[::1\]|192\.168\.\d+\.\d+|10\.\d+\.\d+\.\d+|172\.(1[6-9]|2\d|3[0-1])\.\d+\.\d+)$/i.test(host) || host.endsWith('.local');
}

/**
 * Session Lifecycle & Deduplication Configuration
 * Standard 30-minute rolling session window for anonymous web analytics across tabs.
 */
const SESSION_TTL_MS = 30 * 60 * 1000;
const STORAGE_KEY_SESSION_ID = 'kz_sess_id';
const STORAGE_KEY_LAST_ACTIVE = 'kz_sess_active';
const STORAGE_KEY_EVENTS = 'kz_sess_events';

function getStorage() {
  if (typeof window === 'undefined') return null;
  try {
    if (window.localStorage) return window.localStorage;
  } catch {}
  try {
    if (window.sessionStorage) return window.sessionStorage;
  } catch {}
  return null;
}

/**
 * Retrieves or establishes the active visitor session ID
 */
export function getOrCreateSession() {
  const storage = getStorage();
  if (!storage) {
    return { sessionId: 'anon-session', isNewSession: false };
  }

  try {
    const now = Date.now();
    let sessionId = storage.getItem(STORAGE_KEY_SESSION_ID);
    const lastActive = parseInt(storage.getItem(STORAGE_KEY_LAST_ACTIVE) || '0', 10);
    let isNewSession = false;

    // If session does not exist or has expired past TTL (30 min inactivity), start a clean session
    if (!sessionId || (now - lastActive > SESSION_TTL_MS)) {
      if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
        sessionId = crypto.randomUUID().replace(/-/g, '').slice(0, 24);
      } else {
        sessionId = Math.random().toString(36).slice(2) + Date.now().toString(36);
      }
      storage.setItem(STORAGE_KEY_SESSION_ID, sessionId);
      storage.setItem(STORAGE_KEY_EVENTS, JSON.stringify({}));
      isNewSession = true;
    }

    // Refresh rolling activity timestamp
    storage.setItem(STORAGE_KEY_LAST_ACTIVE, String(now));
    return { sessionId, isNewSession };
  } catch {
    return { sessionId: 'anon-session', isNewSession: false };
  }
}

/**
 * Checks whether this session has already registered this event on this tool
 */
export function hasEventOccurredInSession(toolId, eventName) {
  const storage = getStorage();
  if (!storage) return false;

  try {
    const raw = storage.getItem(STORAGE_KEY_EVENTS);
    if (!raw) return false;
    const records = JSON.parse(raw);
    const key = `${toolId}::${eventName}`;
    return Boolean(records[key]);
  } catch {
    return false;
  }
}

/**
 * Marks an event as recorded within the active visitor session
 */
export function markEventOccurredInSession(toolId, eventName) {
  const storage = getStorage();
  if (!storage) return;

  try {
    const raw = storage.getItem(STORAGE_KEY_EVENTS);
    const records = raw ? JSON.parse(raw) : {};
    const key = `${toolId}::${eventName}`;
    records[key] = Date.now();
    storage.setItem(STORAGE_KEY_EVENTS, JSON.stringify(records));
  } catch {}
}

let lastDispatchTime = 0;
const THROTTLE_INTERVAL_MS = 1500;

/**
 * Dispatches an anonymous tool event with deduplication guardrails
 * @param {string} eventName One of the approved lifecycle events
 * @param {string} toolId Unique tool slug
 */
export function trackEvent(eventName, toolId) {
  try {
    // Strictly ignore events from local test environments so developer testing does not skew analytics
    if (isLocalEnvironment()) {
      return;
    }

    if (!VALID_EVENTS.has(eventName) || !toolId || typeof toolId !== 'string') {
      return;
    }

    // Deduplication Guardrail:
    // A single visitor session can only register a specific lifecycle event (view, start, complete, promo)
    // ONCE per tool. Switching to another tool permits events for that distinct tool.
    if (hasEventOccurredInSession(toolId, eventName)) {
      return;
    }

    // Client-side Throttling Guardrail:
    // Enforces at least 2 seconds between event dispatches to prevent UI spam from overloading the DB
    const now = Date.now();
    if (now - lastDispatchTime < THROTTLE_INTERVAL_MS) {
      return;
    }
    lastDispatchTime = now;

    const { sessionId, isNewSession } = getOrCreateSession();
    markEventOccurredInSession(toolId, eventName);

    const device = detectClientDevice();
    let tz = '';
    try {
      tz = Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || '';
    } catch {}

    const lang = (typeof navigator !== 'undefined' && navigator.language) ? navigator.language : '';

    const payload = JSON.stringify({
      event: eventName,
      tool: toolId,
      session_id: sessionId,
      is_new_session: isNewSession,
      device,
      tz,
      lang
    });

    const endpoint = '/api/events';

    // Primary: fetch with keepalive (immediate & reliable across all modern browsers)
    if (typeof fetch === 'function') {
      fetch(endpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
        credentials: 'same-origin'
      }).catch(() => {
        // Fallback to sendBeacon if fetch fails
        if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
          const blob = new Blob([payload], { type: 'text/plain' });
          navigator.sendBeacon(endpoint, blob);
        }
      });
      return;
    }

    // Fallback if fetch is unavailable
    if (typeof navigator !== 'undefined' && typeof navigator.sendBeacon === 'function') {
      const blob = new Blob([payload], { type: 'text/plain' });
      navigator.sendBeacon(endpoint, blob);
    }
  } catch {
    // Fail silently to never degrade user experience
  }
}

